import * as PropTypes from "prop-types";
import classNames from "classnames";
import circle from "@turf/circle";
import MapGL from "react-map-gl";
import { Motion, spring } from "react-motion";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";
import "mapbox-gl/dist/mapbox-gl.css";
import isEqual from "react-fast-compare";

import rangeFinderMapStyles from "./RangeFinderMap.css";
import { MOTION_PRESETS } from "/react/constants/motionPresets";

import { withViewport } from "/react/utils/withViewport";

let DeckGL, PolygonLayer, TextLayer;

async function loadDeckGl() {
  const module = await import("deck.gl");
  DeckGL = module.DeckGL;
  PolygonLayer = module.PolygonLayer;
  TextLayer = module.TextLayer;
}

const ACCESS_TOKEN =
  "pk.eyJ1Ijoid2luZ2FyZCIsImEiOiJja25ydXN5dXUwcGNnMzFvOXl4Znh4a2NrIn0.5tqqoZvXGKY-j9rp9Intrw";

// const ZOOM_NARROW = 3;
const ZOOM_NARROW = 2.85;
const ZOOM_WIDE = 1.8;
const PITCH_DEFAULT = 0;
const PITCH_HIGH = 51.2;

// const RANGE_IN_KM = 3055.4 - 200; // = 1700 nautical miles. We use a lower value, because the "real" range does not seem to be correct, but the calculation is. So it's weird.
const RANGE_IN_KM = 3055.4 - 300;

const CITIES = [
  {
    name: "New York",
    longitude: -74.0059728,
    latitude: 40.7127753,
  },
  {
    name: "Denver",
    longitude: -104.990251,
    latitude: 39.7392358,
  },
  {
    name: "Chicago",
    longitude: -87.6297982,
    latitude: 41.8781136,
  },
  {
    name: "Bend",
    longitude: -121.3153096,
    latitude: 44.0581728,
  },
  {
    name: "Los Angeles",
    longitude: -118.2436849,
    latitude: 34.0522342,
  },
  {
    name: "San Francisco",
    longitude: -122.4194155,
    latitude: 37.7749295,
  },
  {
    name: "Mexico City",
    longitude: -99.133208,
    latitude: 19.4326077,
  },
  {
    name: "Panama City",
    longitude: -79.5198696,
    latitude: 8.9823792,
  },
  {
    name: "Havana",
    longitude: -82.3665956,
    latitude: 23.1135925,
  },
  {
    name: "State of São Paulo",
    longitude: -46.6291845,
    latitude: -23.5431786,
  },
  {
    name: "Lima",
    longitude: -77.042754,
    latitude: -12.0463731,
  },
  {
    name: "Buenos Aires",
    longitude: -58.3815591,
    latitude: -34.6036844,
  },
  {
    name: "Johannesburg",
    longitude: 28.0473051,
    latitude: -26.2041028,
  },
  {
    name: "Cairo",
    longitude: 31.2357116000001,
    latitude: 30.0444196,
  },
  {
    name: "Casablanca",
    longitude: -7.58984340000006,
    latitude: 33.5731104,
  },
  {
    name: "Nairobi",
    longitude: 36.8219462,
    latitude: -1.2920659,
  },
  {
    name: "Lagos",
    longitude: 3.37920570000006,
    latitude: 6.5243793,
  },
  {
    name: "Dakar",
    longitude: -17.4676861,
    latitude: 14.716677,
  },
  {
    name: "London",
    longitude: -0.127758299999982,
    latitude: 51.5073509,
  },
  {
    name: "Paris",
    longitude: 2.35222190000002,
    latitude: 48.856614,
  },
  {
    name: "Barcelona",
    longitude: 2.17340349999995,
    latitude: 41.3850639,
  },
  {
    name: "Rome",
    longitude: 12.4963655,
    latitude: 41.9027835,
  },
  {
    name: "Berlin",
    longitude: 13.404954,
    latitude: 52.5200066,
  },
  {
    name: "Amsterdam",
    longitude: 4.90356139999994,
    latitude: 52.3679843,
  },
  {
    name: "Istanbul",
    longitude: 28.9783589,
    latitude: 41.0082376,
  },
  {
    name: "Moscow",
    longitude: 37.6172999,
    latitude: 55.755826,
  },
  {
    name: "Oslo",
    longitude: 10.7522454,
    latitude: 59.9138688,
  },
  {
    name: "Tehran",
    longitude: 51.3889736,
    latitude: 35.6891975,
  },
  {
    name: "Delhi",
    longitude: 77.1024901999999,
    latitude: 28.7040592,
  },
  {
    name: "Bangkok",
    longitude: 100.5017651,
    latitude: 13.7563309,
  },
  {
    name: "Singapore",
    longitude: 103.819836,
    latitude: 1.352083,
  },
  {
    name: "Perth",
    longitude: 115.8604572,
    latitude: -31.9505269,
  },
  {
    name: "Sydney",
    longitude: 151.2092955,
    latitude: -33.8688197,
  },
  {
    name: "Jakarta",
    longitude: 106.8650395,
    latitude: -6.17511,
  },
  {
    name: "Guangzhou",
    longitude: 113.264385,
    latitude: 23.12911,
  },
  {
    name: "Tokyo",
    longitude: 139.6917064,
    latitude: 35.6894875,
  },
  {
    name: "Irkutsk",
    longitude: 104.3050183,
    latitude: 52.2869741,
  },
  {
    name: "Yakutsk",
    longitude: 129.6754745,
    latitude: 62.0354523,
  },
  {
    name: "Manila",
    longitude: 120.9842195,
    latitude: 14.5995124,
  },
  {
    name: "Beijing",
    longitude: 116.4073963,
    latitude: 39.9041999,
  },
  {
    name: "Calgary",
    longitude: -114.0708459,
    latitude: 51.0486151,
  },
  {
    name: "Montreal",
    longitude: -73.567256,
    latitude: 45.5016889,
  },
  {
    name: "Orlando",
    longitude: -81.3792365,
    latitude: 28.5383355,
  },
  {
    name: "Dallas",
    longitude: -96.7969879,
    latitude: 32.7766642,
  },
  {
    name: "Novosibirsk",
    longitude: 82.9357327,
    latitude: 55.0083526,
  },
];

@withViewport()
export class RangeFinderMap extends React.Component {
  static propTypes = {
    isActivated: PropTypes.bool,
    selectedCity: PropTypes.string,
    defaultCenter: PropTypes.object,
  };

  state = {
    isReady: false,
    ...getInitialState(this.props),
  };

  springConfig = MOTION_PRESETS.SLOW;

  componentDidUpdate(prevProps) {
    // If we activated the map
    if (!prevProps.isActivated && this.props.isActivated) {
      this.setRangeFromEpicOffice();
    }

    // When we deactivated the map
    if (prevProps.isActivated && !this.props.isActivated) {
      this.setState(getInitialState(this.props));
    }

    if (prevProps.selectedCity !== this.props.selectedCity) {
      if (this.props.selectedCity) {
        this.geocoder.query(this.props.selectedCity);
      }
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return !(isEqual(this.props, nextProps) && isEqual(this.state, nextState));
  }

  async componentDidMount() {
    this.mounted = true;

    const [MapboxGeocoder] = await Promise.all([
      import("@mapbox/mapbox-gl-geocoder").then((x) => x.default),
      loadDeckGl(),
    ]);

    if (!this.mounted) return;

    this.setState({ isReady: true });

    this.mapRef.getMap().addControl(
      (this.geocoder = new MapboxGeocoder({
        accessToken: ACCESS_TOKEN,
        flyTo: false,
        language: "en",
        limit: 1,
        types: "place",
      }))
    );

    this.geocoder.on("results", this.handleGeocoderResult);
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  handleGeocoderResult = (event) => {
    if (event && event.features.length) {
      // Only get places, not other points of interest
      const places = event.features.filter((feature) => {
        return feature.place_type.includes("place");
      });

      if (places.length) {
        const centerCoords = places[0].center; // Just center at the first suggestion
        this.centerAtCoords(centerCoords[1], centerCoords[0], places[0].text);
      } else {
        this.showCityNotFoundMessage();
      }
    } else {
      this.showCityNotFoundMessage();
    }
  };

  showCityNotFoundMessage() {
    window.alert(
      `No cities found for "${this.props.selectedCity}". Try a different city.`
    );
  }

  handleViewportChange = ({ latitude, longitude, bearing, pitch, zoom }) => {
    this.setState({
      viewport: {
        latitude,
        longitude,
        bearing,
        pitch,
        zoom,
      },
    });
  };

  handleOnClick = (event) => {
    this.setState({
      viewport: {
        ...this.state.viewport,
        longitude: event.lngLat[0],
        latitude: event.lngLat[1],
      },
      rangeCenter: {
        longitude: event.lngLat[0],
        latitude: event.lngLat[1],
        city: null, // Don't show a city label when the user clicks on the map
      },
    });

    const centerPoint = {
      longitude: event.lngLat[0],
      latitude: event.lngLat[1],
    };

    this.setCitiesInRage(centerPoint);
  };

  centerAtCoords(latitude, longitude, city = null) {
    this.setState({
      viewport: {
        ...this.state.viewport,
        latitude: latitude,
        longitude: longitude,
        bearing: 10,
      },
      rangeCenter: {
        latitude: latitude,
        longitude: longitude,
        city: city,
      },
    });
  }

  setRangeFromEpicOffice() {
    this.setState({
      viewport: {
        bearing: 0,
        zoom: this.props.viewportIsTabletOrSmaller ? ZOOM_WIDE : ZOOM_NARROW,
        pitch: this.props.viewportIsTabletOrSmaller
          ? PITCH_DEFAULT
          : PITCH_HIGH,
        longitude: this.props.defaultCenter.longitude,
        latitude: this.props.defaultCenter.latitude,
      },
      rangeCenter: {
        longitude: this.props.defaultCenter.longitude,
        latitude: this.props.defaultCenter.latitude,
        city: this.props.defaultCenter.city,
      },
    });
  }

  getCircles(center) {
    // "With a range of over 1650 nautical miles"
    const radius = RANGE_IN_KM;
    const units = "kilometers";
    const steps = 140;
    const amountOfCircles = 5;

    const circles = Array.from(Array(amountOfCircles))
      .map((_, i) => i + 1)
      .map((i) => ({
        polygon: circle(center, (radius / amountOfCircles) * i, {
          steps,
          units,
        }).geometry.coordinates[0],
        lineColor:
          i < amountOfCircles ? [255, 255, 255, 125] : [237, 187, 129, 255], // alpha: 250 = filled, 0 = transparent
      }));

    const outerCircle = [
      {
        polygon: circle(center, radius, { steps, units }).geometry
          .coordinates[0],
        lineColor: [237, 187, 129, 255],
        fillColor: [255, 255, 255, 20],
      },
    ];

    const bullsEye = [
      {
        polygon: circle(center, 20, { steps, units }).geometry.coordinates[0],
        fillColor: [255, 255, 255, 255],
      },
    ];

    return [
      new PolygonLayer({
        id: "bullsEye",
        data: bullsEye,
        lineDashJustified: true,
        lineJointRounded: true,
        filled: true,
        stroked: false,
      }),
      new PolygonLayer({
        id: "circles",
        data: circles,
        lineDashJustified: true,
        lineJointRounded: true,
        filled: false,
        stroked: true,
        lineWidthMinPixels: 1,
        elevation: 1000,
      }),
      new PolygonLayer({
        id: "outerCircle",
        data: outerCircle,
        lineDashJustified: true,
        lineJointRounded: true,
        filled: true,
        stroked: true,
        lineWidthMinPixels: 2,
      }),
    ];
  }

  getLayers() {
    const {
      rangeCenter: { latitude, longitude },
    } = this.state;

    if (longitude && latitude) {
      const center = [longitude, latitude];

      const cityLayers = this.state.citiesOnMap.map((city, index) => {
        // Prevent duplicate text layers, which may look weird when they stack
        const textLayerName = this.getTextLayer(center)
          ? this.getTextLayer(center).props.data[0].name
          : null;

        if (textLayerName !== city.name) {
          return this.getCityLayers([city.longitude, city.latitude], city.name);
        }
      });

      return [
        this.getTextLayer(center),
        ...this.getCircles(center),
        ...cityLayers,
      ];
    }
  }

  getCustomCharacterSet = (string) => {
    // Because the TextLayer of the used plugin does not recognize special characters (like: é)
    // We use the characterSet prop to add the characters we need to render
    // Fixing a bug where "Liège" renders "Li ge"
    // CUSTOM_CHARACTER_SET is an array, like: ["L", "i", "è", "g", "e"]
    let CUSTOM_CHARACTER_SET = [];
    for (let i = 0; i < string.length; i++) {
      CUSTOM_CHARACTER_SET.push(string.charAt(i));
    }
    return CUSTOM_CHARACTER_SET;
  };

  getTextLayer = (centerCoords) => {
    const { rangeCenter } = this.state;

    if (rangeCenter.city) {
      const centerPoint = {
        longitude: centerCoords[0],
        latitude: centerCoords[1],
      };

      this.setCitiesInRage(centerPoint);

      const textLayer = new TextLayer({
        id: "text-layer",
        data: [{ name: rangeCenter.city, coordinates: centerCoords }],
        pickable: false,
        getPosition: (d) => d.coordinates,
        getText: (d) => d.name,
        characterSet: this.getCustomCharacterSet(rangeCenter.city),
        getColor: [255, 255, 255, 255],
        fontFamily: "Helvetica, Aria, sans-serif",
        getSize: 48,
        getAngle: 0,
        getTextAnchor: "middle",
        getAlignmentBaseline: "center",
        getPixelOffset: [0, -40],
      });
      return textLayer;
    }
  };

  // Returns a label and dot to plot on the map
  getCityLayers = (centerCoords, label) => {
    const textLayer = new TextLayer({
      id: `city-layer-${label}`,
      data: [{ name: label, coordinates: centerCoords }],
      pickable: false,
      getPosition: (d) => d.coordinates,
      getText: (d) => d.name,
      characterSet: this.getCustomCharacterSet(label),
      getColor: [255, 255, 255, 255],
      fontFamily: "Helvetica, Aria, sans-serif",
      getSize: 48,
      getAngle: 0,
      getTextAnchor: "middle",
      getAlignmentBaseline: "center",
      getPixelOffset: [0, -40],
    });

    const dotLayer = new PolygonLayer({
      id: `dot-${label}`,
      data: [
        {
          polygon: circle(centerCoords, 20, { steps: 140, units: "kilometers" })
            .geometry.coordinates[0],
          fillColor: [255, 255, 255, 255],
        },
      ],
      lineDashJustified: true,
      lineJointRounded: true,
      filled: true,
      stroked: false,
    });

    return [textLayer, dotLayer];
  };

  setCitiesInRage = (centerPoint) => {
    const citiesOnMap = CITIES.filter((city, index) => {
      return this.arePointsNear(city, centerPoint);
    }, []);

    this.setState({
      citiesOnMap: citiesOnMap,
    });
  };

  arePointsNear = (city, centerPoint) => {
    // Method to determine if a lgn lat value is within a certain radius
    // Credits: https://stackoverflow.com/a/24680708/3194288
    var ky = 40000 / 360;
    var kx = Math.cos((Math.PI * centerPoint.latitude) / 180.0) * ky;
    var dx = Math.abs(centerPoint.longitude - city.longitude) * kx;
    var dy = Math.abs(centerPoint.latitude - city.latitude) * ky;
    return Math.sqrt(dx * dx + dy * dy) <= RANGE_IN_KM;
  };

  render() {
    const { isActivated, viewportWidth, viewportHeight } = this.props;
    const { viewport, mapOptions, isReady } = this.state;

    if (!isReady) return null;

    return (
      <div className={rangeFinderMapStyles.container}>
        <div
          className={classNames(rangeFinderMapStyles.map, {
            [rangeFinderMapStyles.isLocked]: !isActivated,
          })}
        >
          <Motion
            style={{
              longitude: spring(viewport.longitude, this.springConfig),
              latitude: spring(viewport.latitude, this.springConfig),
              bearing: spring(viewport.bearing, this.springConfig),
              zoom: spring(viewport.zoom, this.springConfig),
              pitch: spring(viewport.pitch, this.springConfig),
            }}
          >
            {(interpolatedViewport) => {
              return (
                <MapGL
                  {...interpolatedViewport}
                  {...mapOptions}
                  ref={(node) => (this.mapRef = node)}
                  width={viewportWidth}
                  height={viewportHeight}
                  onViewportChange={this.handleViewportChange}
                  onClick={this.handleOnClick}
                  mapboxApiAccessToken={ACCESS_TOKEN}
                  //mapStyle="mapbox://styles/wingard/ckwl3y4nx3ol214nih1kce9u9"
                  mapStyle="mapbox://styles/wingard/cl3ulzri7008r14rwxqutdm1n"
                >
                  <DeckGL {...interpolatedViewport} layers={this.getLayers()} />
                </MapGL>
              );
            }}
          </Motion>
        </div>
      </div>
    );
  }
}

function getInitialState(props) {
  return {
    mapOptions: {
      scrollZoom: false,
      dragPan: true,
      dragRotate: false,
      doubleClickZoom: false,
      touchZoom: true,
      touchRotate: false,
    },
    viewport: {
      zoom: ZOOM_NARROW,
      pitch: PITCH_DEFAULT,
      bearing: 0,
      longitude: props.defaultCenter.longitude,
      latitude: props.defaultCenter.latitude,
    },
    rangeCenter: {
      longitude: null,
      latitude: null,
      city: null,
    },
    citiesOnMap: [],
  };
}
